import clsx from 'clsx';
import Color from 'color';
import { format as d3Format } from 'd3-format';
import { usePathname } from 'next/navigation';
import numbro from 'numbro';
import { useState } from 'react';

import { isBlank, isFunction, isPresent } from 'shared/lib/utils';
import currencyFormatter from 'shared/lib/currencyFormatter';

import {
  DAY_OF_WEEK_MONTH_DAY_OF_MONTH_YEAR_FORMAT,
  formatAsDate,
} from '@/lib/datetime';
import updateAnnotation from '@/lib/updateAnnotation';
import normalizeAnnotationDate from '@/lib/normalizeAnnotationDate';

import * as Chart from '@/lib/data/chart';
import { isNew } from '@/lib/data/utils';

import DatePickerWithInputField from '@/components/DatePickerWithInputField';

function tagTextColorClassName(annotationBackgroundColor) {
  return Color(annotationBackgroundColor).luminosity() > 0.5
    ? 'text-gray-900'
    : 'text-gray-100';
}

function isEventTargetingField(annotationSet, annotation, fieldMetadata) {
  if (isPresent(annotation.get('targetChartDatasetColumn'))) {
    return (
      annotation.get('targetChartDatasetColumn') === fieldMetadata.get('index')
    );
  } else {
    return (
      annotationSet.get('targetChartDatasetColumn') ===
      fieldMetadata.get('index')
    );
  }
}

export default function AnnotationDateAndValues({
  session,
  graphql,
  dispatch,
  router,
  annotationsRenderer,
  annotation,
  setAnnotation,
  inEditMode,
  setPreventPopoupHide,
  story,
}) {
  const pathname = usePathname();

  const chart = story.get('chart');

  const onFieldMetadataSelection = (fieldMetadata) => (_e) => {
    updateAnnotation(
      session,
      graphql,
      pathname,
      dispatch,
      router,
      annotationsRenderer,
      story,
      annotation,
      {
        targetChartDatasetColumn: fieldMetadata.get('index'),
      },
      // NOTE: needs full refresh due to `redrawSingleAnnotation` not handling
      // `targetChartDatasetColumn` yet.
      {
        skipRedraw: true,
        shouldRefreshRouter: true,
      }
    );
  };

  const [date, setDate] = useState<Date>(new Date(annotation.get('date')));

  const dateString =
    date &&
    formatAsDate(
      date,
      DAY_OF_WEEK_MONTH_DAY_OF_MONTH_YEAR_FORMAT,
      story.getIn(['chart', 'locale'])
    );

  return (
    <div className="flex items-center gap-2">
      {/* Date. */}
      {(() => {
        if (inEditMode) {
          return (
            <DatePickerWithInputField
              story={story}
              date={date}
              setDate={setDate}
              onOpenChange={setPreventPopoupHide}
              onDateSelected={(inputDate) => {
                const utcInputDate = normalizeAnnotationDate(story, inputDate);

                if (isFunction(setAnnotation)) {
                  setAnnotation(
                    annotation.set('date', utcInputDate.toISOString())
                  );
                }

                if (isNew(annotation)) {
                  return;
                }

                return updateAnnotation(
                  session,
                  graphql,
                  pathname,
                  dispatch,
                  router,
                  annotationsRenderer,
                  story,
                  annotation,
                  {
                    date: utcInputDate.toISOString(),
                  },
                  {
                    shouldRefreshRouter: true,
                  }
                );
              }}
            />
          );
        }

        return <p className="">{dateString}</p>;
      })()}

      {/* Values. */}
      {(() => {
        if (
          story.getIn(['chart', 'datasets', 0, 'type']) === 'annotation_set'
        ) {
          return null;
        }

        const selectedYAxes = Chart.getSelectedYAxes(chart);

        return (
          <div
            className={clsx([
              'ml-auto grid grid-cols-2 items-center justify-items-end gap-1 self-end text-right sm:!text-sm',
              selectedYAxes.size > 1 ? 'grid-cols-2' : 'grid-cols-none',
            ])}
          >
            {selectedYAxes
              .sortBy((yAxis) => Chart.yAxesSorter(yAxis))
              .map((yAxis, i) => {
                const yAxisColor = yAxis
                  ? yAxis.getIn(['style', 'strokeColor'])
                  : '#eeeeee';

                const yAxisValueInRow = Chart.getDatasetFieldValueOn(
                  story,
                  yAxis,
                  annotation.get('date')
                );

                let formattedYAxisValueInRow;

                if (yAxisValueInRow === null) {
                  formattedYAxisValueInRow = '-';
                } else {
                  const fieldFormat = Chart.fieldVariableAxisFormat(
                    chart,
                    yAxis
                  );

                  if (isBlank(fieldFormat)) {
                    formattedYAxisValueInRow = numbro(
                      yAxisValueInRow.value
                    ).format({
                      optionalMantissa: true,
                      thousandSeparated: true,
                      mantissa: 1,
                    });
                  } else {
                    formattedYAxisValueInRow = fieldFormat.startsWith('$')
                      ? currencyFormatter(yAxisValueInRow.value)
                      : d3Format(fieldFormat)(yAxisValueInRow.value);
                  }

                  if (yAxisValueInRow.estimated) {
                    formattedYAxisValueInRow = `${formattedYAxisValueInRow} (est.)`;
                  }
                }

                const shouldDifferentiate =
                  selectedYAxes.size > 1 &&
                  isEventTargetingField(
                    story.get('annotationSet'),
                    annotation,
                    yAxis
                  );

                const possiblyOverridenYAxisName =
                  yAxis.getIn(['overrides', 'name']) || yAxis.get('name');

                return (
                  <div
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...(inEditMode && {
                      role: 'button',
                      tabIndex: i,
                      onKeyDown: onFieldMetadataSelection(yAxis),
                      onClick: onFieldMetadataSelection(yAxis),
                    })}
                    key={yAxis.get('index')}
                  >
                    <p
                      className={clsx([
                        'flex items-stretch rounded-[0.4rem] opacity-75',
                        inEditMode && 'hover:opacity-90',
                        shouldDifferentiate && 'border-2',
                      ])}
                      style={{
                        borderColor: yAxisColor,
                      }}
                    >
                      <span
                        key="name"
                        className={clsx([
                          'rounded-l px-2 py-1',
                          tagTextColorClassName(yAxisColor),
                        ])}
                        style={{
                          backgroundColor: yAxisColor,
                        }}
                      >
                        {possiblyOverridenYAxisName}
                      </span>

                      <span
                        key="value"
                        className="rounded-r bg-gray-200 px-2 py-1 font-semibold"
                      >
                        {formattedYAxisValueInRow}
                      </span>
                    </p>
                  </div>
                );
              })}
          </div>
        );
      })()}
    </div>
  );
}
